import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Clock, ChevronLeft, ShoppingBag, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * StorefrontConfigura V2 — Configuratore visivo B2C.
 *
 * Renderizza un prodotto madre componendo i suoi `layers` (SVG paths)
 * sopra la foto base, applicando texture via SVG `<pattern>`.
 *
 * Picker gerarchico per `surface_group`:
 *   - 1 scelta tessuto → applicata a TUTTI i layer del gruppo "upholstery"
 *   - 1 scelta metallo → applicata ai gruppi "frame" / "leg_finish"
 *
 * Fallback se il prodotto non ha blueprint: mostra solo immagine + select base.
 */
export default function StorefrontConfigura() {
  const { slug, sku_madre } = useParams();
  const [tenant, setTenant] = useState(null);
  const [product, setProduct] = useState(null);
  const [tessuti, setTessuti] = useState([]);
  const [gambe, setGambe] = useState([]);
  const [metalli, setMetalli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scelte cliente (1 per surface_group, NON per layer)
  const [chosenTessuto, setChosenTessuto] = useState(null);
  const [chosenPiede, setChosenPiede] = useState(null);
  const [chosenMetallo, setChosenMetallo] = useState(null);
  const [expandedLvl, setExpandedLvl] = useState("LVL1");
  const [expandedFamily, setExpandedFamily] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/storefront/${slug}/configura/${encodeURIComponent(sku_madre)}`);
        const data = r.data;
        setTenant(data.tenant);
        setProduct({
          ...data.modello,
          canvas_config: data.blueprint.canvas_config,
          layers: data.blueprint.layers,
          surface_groups_config: data.blueprint.surface_groups_config,
        });
        setTessuti(data.libraries?.tessuti || []);
        setGambe(data.libraries?.gambe || []);
        setMetalli(data.libraries?.metalli || []);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally { setLoading(false); }
    })();
  }, [slug, sku_madre]);

  // Raggruppa tessuti per LVL → famiglia
  const tessutiByLvl = useMemo(() => {
    const out = {};
    tessuti.forEach((t) => {
      const lvl = t.livello || "LVL1";
      const fam = (t.codice || "").split("-")[0] || t.codice;
      if (!out[lvl]) out[lvl] = {};
      if (!out[lvl][fam]) out[lvl][fam] = [];
      out[lvl][fam].push(t);
    });
    return out;
  }, [tessuti]);

  const prezzoFinale = useMemo(() => {
    const base = parseFloat(product?.prezzo_base || 0);
    const overrides = product?.library_pricing_overrides || {};
    // Override per modello (es. Barka Chair ha sovrapprezzo BARREL-19 = €4.50 invece di €0 globale)
    const ovP = (axis, codice) => {
      const v = overrides?.[axis]?.[(codice || "").toUpperCase()];
      return (v === undefined || v === null) ? null : parseFloat(v);
    };
    const sov = (axis, item) => {
      if (!item) return 0;
      const o = ovP(axis, item.codice);
      return o !== null ? o : parseFloat(item.sovrapprezzo || 0);
    };
    const s1 = sov("tessuti", chosenTessuto);
    const s2 = sov("gambe", chosenPiede);
    const s3 = sov("metalli", chosenMetallo);
    return (base + s1 + s2 + s3).toFixed(2);
  }, [product, chosenTessuto, chosenPiede, chosenMetallo]);

  const fmtEur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

  // Procedural color from string (HSL) — fallback when tessuto has no immagine_url nor hex_color
  const proceduralColor = (key) => {
    if (!key) return "#e5e5e5";
    let h = 0;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) % 360;
    return `hsl(${h}, 45%, 55%)`;
  };

  // Helper: HEX "#RRGGBB" → [r,g,b] in 0-1. Default grigio chiaro se invalido.
  const hexToRgb01 = (hex) => {
    if (!hex || typeof hex !== "string") return [0.9, 0.9, 0.9];
    const h = hex.replace("#", "");
    if (h.length !== 6) return [0.9, 0.9, 0.9];
    return [parseInt(h.slice(0,2),16)/255, parseInt(h.slice(2,4),16)/255, parseInt(h.slice(4,6),16)/255];
  };

  // Config sfondo (può essere personalizzato da Casa Madre nel Surface Editor)
  const bgConfig = product?.background_config || { scale: 1.0, offset_x: 0, offset_y: 0, opacity: 1.0, visible: true, blend_mode: "normal", lock_canvas: false };

  // Derivati dal prodotto / scelte (devono essere dichiarati PRIMA di useEffect che li referenzia)
  const tessutoImg = chosenTessuto?.immagine_url || null;
  const shadingUrl = product?.shading_url || null;
  // Cache-busting: aggiungo updated_at come query string così il browser ricarica la foto a ogni save
  const cacheBust = product?.updated_at ? `?v=${encodeURIComponent(product.updated_at)}` : "";
  const baseImg = product?.immagini?.[0] ? `${product.immagini[0]}${cacheBust}` : null;
  const canvas = product?.canvas_config || { width: 1200, height: 1200 };
  const layers = product?.layers || [];
  const leadMin = product?.lead_time_configuratore_gg || product?.lead_time_gg || 40;
  const leadMax = leadMin + 30;
  const hasBlueprint = layers.length > 0;

  // Dimensioni naturali della foto base (per risolvere disallineamento canvas_config ↔ foto)
  const [naturalSize, setNaturalSize] = useState(null);
  useEffect(() => {
    if (!baseImg) { setNaturalSize(null); return; }
    const img = new Image();
    img.onload = () => setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => setNaturalSize(null);
    img.src = baseImg;
  }, [baseImg]);

  // Il viewBox usa le dimensioni naturali della foto (verità per la posizione); i layer vengono scalati di conseguenza
  const viewW = naturalSize?.w || canvas.width;
  const viewH = naturalSize?.h || canvas.height;
  const layerScaleX = viewW / canvas.width;
  const layerScaleY = viewH / canvas.height;

  // Tipo del tessuto scelto: "tinta_unita" | "fantasia" | "materico"
  const fabricType = chosenTessuto?.fabric_type || "fantasia";
  const displacementScale = chosenTessuto?.meta?.displacement_scale ?? (fabricType === "materico" ? 28 : 14);

  // Restituisce l'item della libreria scelto dall'utente per un asse (per i layer container)
  const chosenForAxis = (axis) => {
    if (axis === "tessuti") return chosenTessuto;
    if (axis === "gambe" || axis === "piedi") return chosenPiede;
    if (axis === "metalli" || axis === "metallo") return chosenMetallo;
    return null;
  };

  // Trick: blend mode per surface_group / material_type — emula luci/ombre della foto base.
  //  - upholstery (tessuti)    → multiply (oscura nelle zone d'ombra, mantiene luminosità)
  //  - leg_finish / metal      → hard-light + screen (preserva riflessi metallici)
  //  - frame / wood (matte)    → multiply
  //  - pelle (leather)         → soft-light (più diffuso, meno aggressivo)
  const BLEND_BY_GROUP = {
    upholstery: "multiply",
    pelle: "soft-light",
    leather: "soft-light",
    leg_finish: "hard-light",
    metal: "hard-light",
    frame: "multiply",
    wood: "multiply",
  };

  // Fase 3 — i layer "glossy" ricevono un secondo passaggio in screen (highlights specular)
  const NEEDS_SPECULAR = new Set(["leg_finish", "metal", "leather", "pelle"]);

  // Risolve il fill da applicare a un layer in base al suo surface_group
  const fillForLayer = (group) => {
    if (group === "upholstery" && chosenTessuto) {
      // Tinta unita: pattern grayscale + feColorMatrix per tingerlo con hex
      if (fabricType === "tinta_unita") {
        if (chosenTessuto.immagine_url) return "url(#pat-tessuto-tinta)";
        // Fallback: hex puro senza texture
        return chosenTessuto.hex_color || proceduralColor(chosenTessuto.codice);
      }
      // Fantasia / Materico: pattern colorato standard
      if (chosenTessuto.immagine_url) return "url(#pat-tessuto)";
      if (chosenTessuto.hex_color) return chosenTessuto.hex_color;
      return proceduralColor(chosenTessuto.codice + (chosenTessuto.codice_colore || ""));
    }
    if ((group === "frame" || group === "leg_finish" || group === "metal") && chosenMetallo) {
      return chosenMetallo.hex_color || proceduralColor(chosenMetallo.codice);
    }
    return "#e5e5e5";
  };

  const submit = async () => {
    if (!chosenTessuto && !chosenMetallo && !chosenPiede) {
      toast.error("Effettua almeno una scelta prima di prenotare");
      return;
    }
    try {
      await api.post(`/public/storefront/${slug}/quote-request`, {
        sku_madre,
        selezioni: {
          tessuto: chosenTessuto ? { codice: chosenTessuto.codice, codice_colore: chosenTessuto.codice_colore } : null,
          piede: chosenPiede ? { codice: chosenPiede.codice } : null,
          metallo: chosenMetallo ? { codice: chosenMetallo.codice } : null,
        },
        prezzo_stimato: parseFloat(prezzoFinale),
      });
      toast.success("Richiesta inviata. Ti contatteremo entro 24h.");
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-neutral-50"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  if (error || !product) return <div className="min-h-screen flex items-center justify-center bg-neutral-50"><div className="text-center"><p className="text-red-700 mb-4">{error || "Modello non disponibile"}</p><Link to={`/s/${slug}`} className="text-sm underline">← Torna alla vetrina</Link></div></div>;

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="storefront-configura-v2">
      {/* WARNING BANNER PRENOTAZIONE */}
      <div className="bg-amber-500 text-amber-950 sticky top-0 z-30" data-testid="prenotazione-banner">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-3">
          <Clock className="w-5 h-5 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-bold uppercase tracking-wider">🕐 Prodotto su Prenotazione — Consegna {leadMin}-{leadMax} giorni</div>
            <div className="text-xs opacity-90">Configurazione su misura. Consegna {leadMin}-{leadMax} giorni dalla conferma dell&apos;ordine.</div>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to={`/s/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-store">
            <ChevronLeft className="w-4 h-4" /> Torna a {tenant?.name}
          </Link>
          <div className="ml-auto text-xs text-muted-foreground font-mono">{sku_madre}</div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-5 gap-8">
        {/* Anteprima a sinistra (3/5) */}
        <div className="lg:col-span-3">
          <div className="overline text-muted-foreground mb-2">{product?.categoria || "Modello"}</div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-2">Personalizza la tua {product?.nome}</h1>
          <p className="text-sm text-muted-foreground mb-6">Scegli tessuto e finitura — il prezzo si aggiorna in tempo reale.</p>
          <div className="bg-white border border-border rounded-sm p-6 sticky top-20">
            {hasBlueprint ? (
              <svg viewBox={`0 0 ${viewW} ${viewH}`} className="w-full" style={{ aspectRatio: `${viewW}/${viewH}` }} data-testid="visual-render">
                <defs>
                  {/* Pattern tessuto standard (fantasia / materico) */}
                  {chosenTessuto?.immagine_url && fabricType !== "tinta_unita" && (
                    <pattern id="pat-tessuto" patternUnits="userSpaceOnUse" width="120" height="120">
                      <image href={tessutoImg} width="120" height="120" preserveAspectRatio="xMidYMid slice" />
                    </pattern>
                  )}

                  {/* Tinta unita: filter feColorMatrix che moltiplica la grayscale per l'HEX scelto */}
                  {chosenTessuto?.immagine_url && fabricType === "tinta_unita" && (() => {
                    const [r, g, b] = hexToRgb01(chosenTessuto.hex_color);
                    // grayscale × tinta: out = L * (R_hex, G_hex, B_hex). L è in R (grayscale ha R=G=B).
                    const matrix = `${r} 0 0 0 0  ${g} 0 0 0 0  ${b} 0 0 0 0  0 0 0 1 0`;
                    return (
                      <>
                        <filter id="fxTinta" colorInterpolationFilters="sRGB">
                          <feColorMatrix type="matrix" values={matrix} />
                        </filter>
                        <pattern id="pat-tessuto-tinta" patternUnits="userSpaceOnUse" width="120" height="120">
                          <image href={tessutoImg} width="120" height="120" preserveAspectRatio="xMidYMid slice" filter="url(#fxTinta)" />
                        </pattern>
                      </>
                    );
                  })()}

                  {/* Fase 3 — Displacement: usa la shading map come height field per "piegare" la texture sui volumi */}
                  {shadingUrl && (
                    <filter id="fxDisplace" x="0" y="0" width="100%" height="100%">
                      <feImage href={shadingUrl} x="0" y="0" width={canvas.width} height={canvas.height} result="hmap" preserveAspectRatio="none" />
                      <feDisplacementMap in="SourceGraphic" in2="hmap" scale={displacementScale} xChannelSelector="R" yChannelSelector="R" />
                    </filter>
                  )}

                  {/* Fase 3 — Specular: highlights diffusi per metalli / pelle */}
                  <filter id="fxSpecular" x="-10%" y="-10%" width="120%" height="120%">
                    <feGaussianBlur in="SourceAlpha" stdDeviation="3" result="blur" />
                    <feSpecularLighting in="blur" surfaceScale="4" specularConstant="0.9" specularExponent="22" lightingColor="#ffffff" result="spec">
                      <feDistantLight azimuth="225" elevation="60" />
                    </feSpecularLighting>
                    <feComposite in="spec" in2="SourceAlpha" operator="in" result="specClip" />
                  </filter>

                  {/* Fase 1 — Soft light bloom (subtle volume) */}
                  <filter id="fxBloom" x="-5%" y="-5%" width="110%" height="110%">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="2" />
                  </filter>
                </defs>

                {/* Fase 1 — La foto base contiene già luci/ombre reali; viene moltiplicata sopra i layer.
                    background_config consente a Casa Madre di gestire l'immagine come un layer (scale, offset, opacity, hide). */}
                {baseImg && bgConfig.visible !== false && (bgConfig.opacity ?? 1) > 0 && (() => {
                  const bgScale = bgConfig.scale || 1;
                  const bgOx = bgConfig.offset_x || 0;
                  const bgOy = bgConfig.offset_y || 0;
                  // La foto riempie il viewBox naturale per definizione, poi applicabile scale/offset utente
                  const w = viewW * bgScale;
                  const h = viewH * bgScale;
                  const x = (viewW - w) / 2 + bgOx;
                  const y = (viewH - h) / 2 + bgOy;
                  return (
                    <image href={baseImg} x={x} y={y} width={w} height={h}
                      preserveAspectRatio="xMidYMid slice"
                      opacity={bgConfig.opacity ?? 1}
                      style={{ mixBlendMode: bgConfig.blend_mode || "normal" }}
                      data-testid="render-base-photo" />
                  );
                })()}

                {/* Wrapper layer con scale per allineare a foto base se canvas_config != naturali */}
                <g transform={`scale(${layerScaleX} ${layerScaleY})`} data-testid="layers-wrap">

                {/* Layer ordinati per z_index — ognuno con il proprio blend mode */}
                {[...layers].sort((a, b) => (a.z_index || 0) - (b.z_index || 0)).map((l) => {
                  const layerType = l.layer_type || "mask";

                  // ─── LAYER SHADING: PNG luci/ombre come overlay editabile ───
                  if (layerType === "shading" && l.image_url) {
                    const href = l.image_url.startsWith("http") ? l.image_url : `${process.env.REACT_APP_BACKEND_URL || ""}${l.image_url}`;
                    return (
                      <g key={l.id} style={{ mixBlendMode: l.blend_mode || "multiply", opacity: l.opacity ?? 0.7 }} data-testid={`render-layer-shading-${l.id}`}>
                        <image href={href} x={l.image_x || 0} y={l.image_y || 0}
                          width={l.image_w || canvas.width} height={l.image_h || canvas.height}
                          preserveAspectRatio="none" />
                      </g>
                    );
                  }

                  // ─── LAYER CONTAINER: mostra il PNG dell'item scelto nella libreria (gambe, piedi, ecc) ───
                  if (layerType === "container") {
                    const cfg = l.container_config || {};
                    const axis = cfg.library_axis || "gambe";
                    const item = chosenForAxis(axis);
                    if (!item?.immagine_url) return null;
                    // Se è definito un bbox usalo come "frame", altrimenti scala fissa attorno all'anchor
                    let x, y, w, h;
                    if (Array.isArray(cfg.bbox) && cfg.bbox.length === 4) {
                      const [x0, y0, x1, y1] = cfg.bbox;
                      x = x0; y = y0; w = x1 - x0; h = y1 - y0;
                    } else if (cfg.anchor_x !== undefined && cfg.anchor_y !== undefined) {
                      const s = cfg.scale || 0.3;
                      w = canvas.width * s;
                      h = canvas.height * s;
                      x = cfg.anchor_x - w / 2;
                      y = cfg.anchor_y - h / 2;
                    } else {
                      // Fallback: copre 30% al centro
                      w = canvas.width * 0.3;
                      h = canvas.height * 0.3;
                      x = (canvas.width - w) / 2;
                      y = (canvas.height - h) / 2;
                    }
                    return (
                      <g key={l.id} data-testid={`render-layer-container-${l.id}`}>
                        <image href={item.immagine_url} x={x} y={y} width={w} height={h}
                          preserveAspectRatio="xMidYMid meet" />
                      </g>
                    );
                  }

                  // ─── LAYER MASCHERA (default): applica colore/texture sopra la foto ───
                  const fill = fillForLayer(l.surface_group);
                  const group = l.surface_group || "upholstery";
                  // Blend mode: se il layer ha blend_mode esplicito (Photoshop-like) usalo, altrimenti default per gruppo
                  const blend = l.blend_mode || BLEND_BY_GROUP[group] || "multiply";
                  const useDisplace = shadingUrl && group === "upholstery";
                  return (
                    <g key={l.id} style={{ mixBlendMode: blend, opacity: l.opacity ?? 1 }} data-testid={`render-layer-${l.id}`}>
                      <path d={l.svg_path} fill={fill}
                        filter={useDisplace ? "url(#fxDisplace)" : undefined}
                        stroke="rgba(0,0,0,0.08)" strokeWidth="1.5" />
                    </g>
                  );
                })}

                {/* Fase 3 — Specular highlights solo per layer "glossy" (metalli/pelle) */}
                {chosenMetallo && (
                  <g style={{ mixBlendMode: "screen", opacity: 0.4 }} data-testid="render-specular">
                    {layers.filter((l) => NEEDS_SPECULAR.has(l.surface_group || "")).map((l) => (
                      <path key={`spec-${l.id}`} d={l.svg_path} fill="#ffffff" filter="url(#fxSpecular)" />
                    ))}
                  </g>
                )}

                </g>

                {/* Fase 2 — Re-applica la shading map sopra per ricomporre luci/ombre */}
                {shadingUrl && (
                  <image href={shadingUrl} x="0" y="0" width={viewW} height={viewH}
                    preserveAspectRatio="xMidYMid slice"
                    style={{ mixBlendMode: "multiply", opacity: 0.55 }} data-testid="render-shading" />
                )}
              </svg>
            ) : (
              <div className="aspect-square bg-neutral-100 rounded-sm flex items-center justify-center text-muted-foreground text-sm">
                <div className="text-center">
                  {product?.immagini?.[0] ? <img src={product.immagini[0]} alt={product.nome} className="max-w-full max-h-full object-contain" /> : "Nessuna anteprima disponibile"}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Picker a destra (2/5) */}
        <div className="lg:col-span-2 space-y-4">
          {/* TESSUTO */}
          <div className="bg-white border border-border rounded-sm" data-testid="picker-tessuto">
            <div className="px-4 py-3 border-b border-border">
              <div className="overline text-muted-foreground">1. Tessuto imbottitura</div>
              <div className="text-sm font-bold">
                {chosenTessuto ? `${chosenTessuto.codice} ${chosenTessuto.nome_colore ? `· ${chosenTessuto.nome_colore}` : ""}` : "Scegli un tessuto"}
              </div>
            </div>
            <div className="p-3 max-h-96 overflow-auto">
              {Object.keys(tessutiByLvl).sort().map((lvl) => (
                <div key={lvl} className="mb-2">
                  <button onClick={() => setExpandedLvl(expandedLvl === lvl ? null : lvl)}
                    className="w-full px-2 py-1.5 bg-neutral-100 rounded-sm text-left text-xs font-bold flex items-center justify-between hover:bg-neutral-200">
                    <span>📂 {lvl} {lvl === "LVL1" ? "(+€0)" : lvl === "LVL2" ? "(+€1.40)" : lvl === "LVL3" ? "(+€2.10)" : lvl === "LVL4" ? "(+€2.80)" : "(+€3.50)"}</span>
                    <span>{expandedLvl === lvl ? "▾" : "▸"}</span>
                  </button>
                  {expandedLvl === lvl && Object.keys(tessutiByLvl[lvl]).sort().map((fam) => (
                    <div key={fam} className="ml-2 mt-1">
                      <button onClick={() => setExpandedFamily(expandedFamily === fam ? null : fam)} className="w-full px-2 py-1 text-left text-[11px] font-medium hover:bg-accent rounded-sm flex justify-between">
                        <span>↳ {fam} ({tessutiByLvl[lvl][fam].length} colori)</span>
                        <span>{expandedFamily === fam ? "▾" : "▸"}</span>
                      </button>
                      {expandedFamily === fam && (
                        <div className="grid grid-cols-6 gap-1 mt-1 ml-3">
                          {tessutiByLvl[lvl][fam].map((t) => {
                            const tType = t.fabric_type || "fantasia";
                            const bgColor = t.hex_color || proceduralColor(t.codice + (t.codice_colore || ""));
                            // Per tinta_unita lo swatch è SEMPRE il colore puro (la grayscale è solo la texture)
                            // Per fantasia/materico mostra la texture se presente
                            const bgStyle = (tType !== "tinta_unita" && t.immagine_url)
                              ? { background: `center/cover no-repeat url(${t.immagine_url})` }
                              : { background: bgColor };
                            return (
                              <button key={t.codice} onClick={() => setChosenTessuto(t)} title={`${t.codice} ${t.nome_colore || ""}${tType !== "fantasia" ? ` · ${tType}` : ""}`}
                                data-testid={`swatch-${t.codice}`}
                                className={`aspect-square rounded-sm border-2 transition-all ${chosenTessuto?.codice === t.codice ? "border-brand ring-2 ring-brand/30" : "border-border hover:border-foreground"}`}
                                style={bgStyle}>
                                <span className="sr-only">{t.codice}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* PIEDE */}
          {gambe.length > 0 && (
            <div className="bg-white border border-border rounded-sm" data-testid="picker-piede">
              <div className="px-4 py-3 border-b border-border">
                <div className="overline text-muted-foreground">2. Piede / Base</div>
                <div className="text-sm font-bold">{chosenPiede ? chosenPiede.nome : "Scegli un piede"}</div>
              </div>
              <div className="p-3 grid grid-cols-2 gap-1.5">
                {gambe.map((g) => (
                  <button key={g.codice} onClick={() => setChosenPiede(g)} data-testid={`piede-${g.codice}`}
                    className={`px-2 py-2 rounded-sm border text-xs text-left transition-colors ${chosenPiede?.codice === g.codice ? "border-brand bg-brand/10" : "border-border hover:bg-accent"}`}>
                    <div className="font-medium truncate">{g.nome}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">{g.codice}{g.sovrapprezzo > 0 ? ` · +€${g.sovrapprezzo}` : ""}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* METALLO */}
          {metalli.length > 0 && (
            <div className="bg-white border border-border rounded-sm" data-testid="picker-metallo">
              <div className="px-4 py-3 border-b border-border">
                <div className="overline text-muted-foreground">3. Finitura metallo</div>
                <div className="text-sm font-bold">{chosenMetallo ? chosenMetallo.nome : "Scegli finitura"}</div>
              </div>
              <div className="p-3 flex flex-wrap gap-2">
                {metalli.map((m) => (
                  <button key={m.codice} onClick={() => setChosenMetallo(m)} title={`${m.nome} (${m.codice})`}
                    data-testid={`metallo-${m.codice}`}
                    className={`w-12 h-12 rounded-sm border-2 transition-all ${chosenMetallo?.codice === m.codice ? "border-brand ring-2 ring-brand/30" : "border-border hover:border-foreground"}`}
                    style={{ background: m.hex_color || proceduralColor(m.codice) }} />
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="bg-foreground text-white rounded-sm p-5 sticky bottom-4">
            <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1">Prezzo configurazione</div>
            <div className="font-bold text-3xl mb-2">{fmtEur(prezzoFinale)}</div>
            <div className="text-[11px] text-amber-300 mb-3">⏱ Consegna {leadMin}-{leadMax} giorni</div>
            <button onClick={submit} data-testid="prenota-btn"
              className="w-full h-12 rounded-sm bg-amber-400 text-foreground text-sm font-bold hover:bg-amber-300 inline-flex items-center justify-center gap-2">
              <ShoppingBag className="w-4 h-4" /> Prenota la configurazione
            </button>
          </div>
        </div>
      </div>

      {/* ─────────────── CARATTERISTICHE & SCHEDA TECNICA ─────────────── */}
      <ProductFeatures product={product}
        chosenTessuto={chosenTessuto} chosenPiede={chosenPiede} chosenMetallo={chosenMetallo} />
    </div>
  );
}

// ─────────────────────── ProductFeatures (vetrina) ───────────────────────
function VariantCard({ label, item, axis }) {
  if (!item) return null;
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const img = item.immagine_url ? (item.immagine_url.startsWith("http") ? item.immagine_url : `${apiBase}${item.immagine_url}`) : null;
  const bg = item.hex_color || "#e5e5e5";
  return (
    <div className="flex items-center gap-3 p-2.5 border border-border rounded-sm bg-white" data-testid={`vetrina-var-${axis}`}>
      <div className="w-12 h-12 rounded-sm border border-border overflow-hidden flex-shrink-0"
        style={img ? { background: `center/cover no-repeat url(${img})` } : { background: bg }} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-sm font-medium truncate">{item.nome || item.codice}</div>
        {item.codice && <div className="text-[10px] text-muted-foreground font-mono">{item.codice}{item.nome_colore ? ` · ${item.nome_colore}` : ""}</div>}
      </div>
    </div>
  );
}

function ProductFeatures({ product, chosenTessuto, chosenPiede, chosenMetallo }) {
  const fv = product?.feature_visibility || {
    descrizione: true, dimensioni: true, materiali: true, specifiche: true,
    varianti_correnti: true, garanzia: true, lead_time: true,
  };
  const dim = product?.dimensioni || {};
  const dimEntries = [
    ["larghezza_cm", "Larghezza", "cm"],
    ["profondita_cm", "Profondità", "cm"],
    ["altezza_cm", "Altezza", "cm"],
    ["diametro_cm", "Diametro Ø", "cm"],
    ["seduta_cm", "H. seduta", "cm"],
    ["peso_kg", "Peso", "kg"],
  ].filter(([k]) => dim[k] !== undefined && dim[k] !== null && dim[k] !== "");

  const mat = product?.materiali || [];
  const specs = product?.specifiche || [];
  const desc = product?.descrizione_long || "";
  const hasDescrizione = fv.descrizione && desc.trim().length > 0;
  const hasDim = fv.dimensioni && dimEntries.length > 0;
  const hasMat = fv.materiali && mat.length > 0;
  const hasSpec = fv.specifiche && specs.length > 0;
  const hasGar = fv.garanzia && product?.garanzia_mesi;
  const hasLead = fv.lead_time && product?.lead_time_gg;
  const hasVar = fv.varianti_correnti && (chosenTessuto || chosenPiede || chosenMetallo);

  if (!hasDescrizione && !hasDim && !hasMat && !hasSpec && !hasGar && !hasLead && !hasVar) {
    return null;
  }

  return (
    <div className="bg-white border-t border-border" data-testid="vetrina-caratteristiche">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-10">
          {/* Colonna sinistra: descrizione + materiali + varianti correnti */}
          <div className="lg:col-span-2 space-y-8">
            {hasDescrizione && (
              <section data-testid="vetrina-descrizione">
                <h2 className="overline text-muted-foreground mb-3">Descrizione</h2>
                <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">{desc}</p>
              </section>
            )}
            {hasMat && (
              <section data-testid="vetrina-materiali">
                <h2 className="overline text-muted-foreground mb-3">Materiali</h2>
                <div className="flex flex-wrap gap-1.5">
                  {mat.map((m, i) => (
                    <span key={i} className="px-2.5 py-1 bg-neutral-100 border border-border rounded-sm text-xs font-medium">{m}</span>
                  ))}
                </div>
              </section>
            )}
            {hasVar && (
              <section data-testid="vetrina-varianti-correnti">
                <h2 className="overline text-muted-foreground mb-3">La tua configurazione</h2>
                <div className="grid sm:grid-cols-3 gap-2">
                  <VariantCard label="Tessuto" item={chosenTessuto} axis="tessuto" />
                  <VariantCard label="Piede / Base" item={chosenPiede} axis="piede" />
                  <VariantCard label="Finitura metallo" item={chosenMetallo} axis="metallo" />
                </div>
              </section>
            )}
          </div>

          {/* Colonna destra: dimensioni + specifiche + garanzia/lead */}
          <aside className="space-y-6">
            {hasDim && (
              <section data-testid="vetrina-dimensioni">
                <h2 className="overline text-muted-foreground mb-3">Dimensioni</h2>
                <dl className="grid grid-cols-2 gap-y-2 gap-x-3 text-sm">
                  {dimEntries.map(([k, label, unit]) => (
                    <React.Fragment key={k}>
                      <dt className="text-muted-foreground text-xs">{label}</dt>
                      <dd className="font-mono font-medium text-right">{dim[k]} <span className="text-[10px] text-muted-foreground">{unit}</span></dd>
                    </React.Fragment>
                  ))}
                </dl>
              </section>
            )}
            {hasSpec && (
              <section data-testid="vetrina-specifiche">
                <h2 className="overline text-muted-foreground mb-3">Specifiche tecniche</h2>
                <dl className="space-y-1 text-sm">
                  {specs.map((s, i) => (
                    <div key={i} className="flex justify-between gap-3 border-b border-border/60 pb-1.5">
                      <dt className="text-muted-foreground text-xs flex-shrink-0">{s.key}</dt>
                      <dd className="font-medium text-right text-xs">{s.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}
            {(hasGar || hasLead) && (
              <section data-testid="vetrina-meta" className="bg-neutral-50 rounded-sm p-4 space-y-2 border border-border">
                {hasGar && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">🛡️ Garanzia</span>
                    <span className="font-medium">{product.garanzia_mesi} mesi</span>
                  </div>
                )}
                {hasLead && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">⏱ Tempi di consegna</span>
                    <span className="font-medium">{product.lead_time_gg} giorni</span>
                  </div>
                )}
              </section>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
